import React, { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { participantsApi, type SignupAnswerRow } from "../api/participants";
import { qk } from "../../../../lib/queryKeys";
import { ROUTES } from "../../../../shared/utils";
import { useClientScope } from "../../../../shared/contexts";
import { buildCsv, downloadCsv } from "../../utils/csv";

const STATUS_OPTIONS = ["Active", "Pending", "On hold", "Completed"];
const FLAGGED_OPTIONS = ["None", "Needs follow-up", "Escalated"];

// A colour tag is stored as a hex string. Validate it so we can render it as an
// actual colour swatch rather than printing the raw code (BT-198).
const isHexColour = (value?: string | null): value is string =>
  !!value && /^#[0-9a-fA-F]{6}$/.test(value.trim());

// Plain-English overview of the participant list columns, shown via the ℹ️
// next to the page title (BT-232).
const COLUMN_OVERVIEW: { label: string; description: string }[] = [
  { label: "Participant", description: "Participant's name and the email they signed up with." },
  { label: "Linked user", description: "The Keycloak/app user account linked to this participant, if one exists." },
  { label: "Company", description: "The client/company the participant belongs to." },
  { label: "Status", description: "Where the participant is in their journey (Active, Pending, On hold, Completed)." },
  { label: "Wear jeopardy", description: "Whether the participant is wearing their device as expected, or at risk (not worn / intermittent)." },
  { label: "Connected", description: "Wearable connection health: connected, reconnect needed (token expired — participant must re-authorise), or not connected." },
  { label: "First connected", description: "The date the participant first connected a wearable and data started flowing." },
  { label: "Flags", description: "Manual review flags and colour tags set against the participant." },
  { label: "Device", description: "The wearable device linked to the participant (e.g. WHOOP)." },
  { label: "Updated", description: "When this participant's record was last updated." },
];

const statusChipStyles: Record<string, { bg: string; text: string }> = {
  Active: { bg: "#dcfce7", text: "#166534" },
  Pending: { bg: "#fef9c3", text: "#854d0e" },
  "On hold": { bg: "#fee2e2", text: "#991b1b" },
  Completed: { bg: "#e0f2fe", text: "#075985" },
};

const wearStatusChipStyles: Record<string, { bg: string; text: string; label: string }> = {
  not_worn: { bg: "#fee2e2", text: "#991b1b", label: "Not worn" },
  intermittent_wear: { bg: "#fef3c7", text: "#92400e", label: "Intermittent" },
  not_connected: { bg: "#e5e7eb", text: "#374151", label: "Not connected" },
  recently_connected: { bg: "#dbeafe", text: "#1e3a8a", label: "Recently connected" },
  wearing_consistently: { bg: "#dcfce7", text: "#166534", label: "Wearing consistently" },
};

// Connected status derived per participant. "reconnect" takes precedence: a
// participant can have connected once (first_connected_at set) but now need to
// re-authorise an expired token (needs_reauth). "not_connected" comes from the
// wear-jeopardy view (no row in whoop_users/fitbit_users).
type ConnectedStatus = "connected" | "reconnect" | "not_connected";

const connectedStatusStyles: Record<
  ConnectedStatus,
  { bg: string; text: string; label: string }
> = {
  connected: { bg: "#dcfce7", text: "#166534", label: "Connected" },
  reconnect: { bg: "#fef3c7", text: "#92400e", label: "Reconnect needed" },
  not_connected: { bg: "#e5e7eb", text: "#374151", label: "Not connected" },
};

const getConnectedStatus = (p: {
  needs_reauth?: boolean | null;
  wear_jeopardy_status?: string | null;
  first_connected_at?: string | null;
}): ConnectedStatus => {
  if (p.needs_reauth) return "reconnect";
  if (p.wear_jeopardy_status === "not_connected" || !p.first_connected_at)
    return "not_connected";
  return "connected";
};

const getStatusChipSx = (status?: string | null) => {
  if (!status) return undefined;
  const p = statusChipStyles[status];
  if (!p) return undefined;
  return { backgroundColor: p.bg, color: p.text, fontWeight: 600 };
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

export const Participants: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isCentralAdmin, effectiveClientId } = useClientScope();

  const [search, setSearch] = useState(searchParams.get("search") ?? "");
  const [status, setStatus] = useState(searchParams.get("status") ?? "");
  const [flagged, setFlagged] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [whoopReceived, setWhoopReceived] = useState<"" | "true" | "false">("");
  const [usingOwnDevice, setUsingOwnDevice] = useState<"" | "true" | "false">("");
  const [programmeState, setProgrammeState] = useState<"" | "on" | "off">("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [headerFilters, setHeaderFilters] = useState({
    participant: "",
    linkedUser: "",
    company: "",
    status: searchParams.get("status") ?? "",
    wearJeopardy: "",
    connected: "",
  });
  const [surveyQuestionFilter, setSurveyQuestionFilter] = useState("");
  const [surveyAnswerFilter, setSurveyAnswerFilter] = useState("");

  const queryParams = useMemo(
    () => ({
      client_id: effectiveClientId || undefined,
      search: search || undefined,
      status: status || undefined,
      flagged: flagged === "None" ? "" : flagged || undefined,
      whoop_received: whoopReceived === "" ? undefined : whoopReceived === "true",
      using_own_device: usingOwnDevice === "" ? undefined : usingOwnDevice === "true",
    }),
    [effectiveClientId, search, status, flagged, whoopReceived, usingOwnDevice]
  );

  const listQuery = useQuery({
    queryKey: qk.participants(queryParams),
    queryFn: () => participantsApi.list(queryParams),
  });
  const participants = listQuery.data?.data ?? [];

  const [loadSurveyAnswers, setLoadSurveyAnswers] = useState(false);
  const surveyAnswersQuery = useQuery({
    queryKey: qk.participantSignupAnswersBulk(effectiveClientId),
    queryFn: () =>
      participantsApi.listSignupAnswersBulk(
        effectiveClientId ? { client_id: effectiveClientId } : undefined
      ),
    enabled: loadSurveyAnswers,
  });
  const surveyAnswers: SignupAnswerRow[] = surveyAnswersQuery.data?.data.answers ?? [];
  const surveyQuestions = surveyAnswersQuery.data?.data.questions ?? [];

  const surveyAnswersByParticipant = useMemo(() => {
    const map = new Map<string, Map<string, string>>();
    surveyAnswers.forEach((row) => {
      if (!map.has(row.participant_id)) map.set(row.participant_id, new Map());
      map.get(row.participant_id)!.set(row.question_id, row.answer ?? "");
    });
    return map;
  }, [surveyAnswers]);

  const filteredParticipants = useMemo(() => {
    const normalize = (value?: string | null) => String(value || "").toLowerCase().trim();
    const isOnProgramme = (p: (typeof participants)[number]) =>
      Boolean(p.status || p.date_started || p.date_added_to_lms);

    return participants.filter((p) => {
      if (companyFilter && normalize(p.company) !== normalize(companyFilter)) return false;
      if (programmeState === "on" && !isOnProgramme(p)) return false;
      if (programmeState === "off" && isOnProgramme(p)) return false;

      const pName = normalize(`${p.first_name ?? ""} ${p.surname ?? ""}`);
      const lUser = normalize(`${p.user_first_name ?? ""} ${p.user_last_name ?? ""} ${p.user_email ?? ""}`);

      if (headerFilters.participant && !pName.includes(normalize(headerFilters.participant)) && !normalize(p.email).includes(normalize(headerFilters.participant))) return false;
      if (headerFilters.linkedUser && !lUser.includes(normalize(headerFilters.linkedUser))) return false;
      if (headerFilters.company && !normalize(p.company).includes(normalize(headerFilters.company))) return false;
      if (headerFilters.status && normalize(p.status) !== normalize(headerFilters.status)) return false;
      if (headerFilters.wearJeopardy && normalize(p.wear_jeopardy_status) !== normalize(headerFilters.wearJeopardy)) return false;
      if (headerFilters.connected && getConnectedStatus(p) !== headerFilters.connected) return false;

      if (surveyQuestionFilter) {
        const answers = surveyAnswersByParticipant.get(p.id);
        const answer = answers?.get(surveyQuestionFilter) ?? "";
        if (surveyAnswerFilter && !answer.toLowerCase().includes(surveyAnswerFilter.toLowerCase())) return false;
        if (!surveyAnswerFilter && !answer) return false;
      }

      return true;
    });
  }, [companyFilter, headerFilters, participants, programmeState, surveyQuestionFilter, surveyAnswerFilter, surveyAnswersByParticipant]);

  const companyOptions = useMemo(() => {
    const unique = new Set(participants.map((p) => (p.company || "").trim()).filter(Boolean));
    return Array.from(unique).sort((a, b) => a.localeCompare(b));
  }, [participants]);

  const companyCounts = useMemo(() => {
    const counts = new Map<string, number>();
    filteredParticipants.forEach((p) => {
      const key = (p.company || "Unassigned").trim() || "Unassigned";
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [filteredParticipants]);

  const notOnProgrammeCount = useMemo(
    () => filteredParticipants.filter((p) => !p.status && !p.date_started && !p.date_added_to_lms).length,
    [filteredParticipants]
  );

  // Count active advanced filters
  const advancedActiveCount = [whoopReceived, usingOwnDevice, programmeState, surveyQuestionFilter].filter(Boolean).length;

  const handleDownloadParticipants = async () => {
    if (!isCentralAdmin || !filteredParticipants.length) return;
    if (!loadSurveyAnswers) setLoadSurveyAnswers(true);
    let answers = surveyAnswers;
    let questions = surveyQuestions;
    if (!surveyAnswersQuery.isFetched) {
      try {
        const result = await participantsApi.listSignupAnswersBulk(
          effectiveClientId ? { client_id: effectiveClientId } : undefined
        );
        answers = result.data.answers;
        questions = result.data.questions;
      } catch {
        // proceed without survey answers
      }
    }
    const answerMap = new Map<string, Map<string, string>>();
    answers.forEach((row) => {
      if (!answerMap.has(row.participant_id)) answerMap.set(row.participant_id, new Map());
      answerMap.get(row.participant_id)!.set(row.question_id, row.answer ?? "");
    });
    const surveyHeaders = questions.map((q) => `survey_${q.title}`);
    const csv = buildCsv(
      ["participant_name", "participant_email", "contact_number", "linked_user_email", "linked_user_name", "company", "status", "flagged", "color_tag", "wear_jeopardy_status", "connected_status", "first_connected_at", "whoop_received", "using_own_device", "updated_at", ...surveyHeaders],
      filteredParticipants.map((p) => {
        const pName = `${p.first_name ?? ""} ${p.surname ?? ""}`.trim();
        const lName = `${p.user_first_name ?? ""} ${p.user_last_name ?? ""}`.trim();
        const pAnswers = answerMap.get(p.id);
        const surveyValues = questions.map((q) => pAnswers?.get(q.id) ?? "");
        return [pName, p.email ?? "", p.contact_number ?? "", p.user_email ?? "", lName, p.company ?? "", p.status ?? "", p.flagged ?? "", p.color_tag ?? "", p.wear_jeopardy_status ?? "", connectedStatusStyles[getConnectedStatus(p)].label, p.first_connected_at ?? "", p.whoop_received ? "Yes" : "No", p.using_own_device ? "Yes" : "No", p.updated_at ?? "", ...surveyValues];
      })
    );
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadCsv(`participants-${stamp}.csv`, csv);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "center" }} spacing={1.5} mb={2.5} sx={{ flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1 }}>
          <Typography variant="h6" fontWeight={700}>
            Participants
          </Typography>
          <Tooltip
            arrow
            title={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.5 }}>
                  Column guide
                </Typography>
                {COLUMN_OVERVIEW.map((c) => (
                  <Typography key={c.label} variant="caption" display="block" sx={{ mb: 0.5 }}>
                    <strong>{c.label}:</strong> {c.description}
                  </Typography>
                ))}
              </Box>
            }
          >
            <IconButton size="small" sx={{ color: "text.secondary" }} aria-label="Column guide">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={1}>
          {isCentralAdmin && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
              disabled={listQuery.isLoading || filteredParticipants.length === 0}
              onClick={handleDownloadParticipants}
            >
              Export
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon fontSize="small" />}
            onClick={() => navigate(`${ROUTES.PARTICIPANTS}/new`)}
          >
            New participant
          </Button>
        </Stack>
      </Stack>

      {/* Filter toolbar */}
      <Stack spacing={1.5} mb={2.5} sx={{ flexShrink: 0 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
          <TextField
            size="small"
            placeholder="Search name, email or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 0 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            select
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            label="Status"
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          <TextField
            select
            size="small"
            value={flagged}
            onChange={(e) => setFlagged(e.target.value)}
            label="Flagged"
            sx={{ minWidth: 130 }}
          >
            <MenuItem value="">All</MenuItem>
            {FLAGGED_OPTIONS.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
          {companyOptions.length > 0 && (
            <TextField
              select
              size="small"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              label="Company"
              sx={{ minWidth: 150 }}
            >
              <MenuItem value="">All</MenuItem>
              {companyOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}
          <Button
            size="small"
            variant={showAdvanced ? "contained" : "outlined"}
            color={advancedActiveCount > 0 ? "primary" : "inherit"}
            startIcon={<FilterListIcon fontSize="small" />}
            onClick={() => setShowAdvanced((v) => !v)}
            sx={{ flexShrink: 0 }}
          >
            More{advancedActiveCount > 0 ? ` (${advancedActiveCount})` : ""}
          </Button>
        </Stack>

        {/* Advanced filters */}
        <Collapse in={showAdvanced}>
          <Box
            sx={{
              p: 1.5,
              bgcolor: "action.hover",
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap" alignItems={{ sm: "flex-start" }}>
              <TextField
                select size="small" value={whoopReceived}
                onChange={(e) => setWhoopReceived(e.target.value as "" | "true" | "false")}
                label="WHOOP received" sx={{ minWidth: 130 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
              <TextField
                select size="small" value={usingOwnDevice}
                onChange={(e) => setUsingOwnDevice(e.target.value as "" | "true" | "false")}
                label="Own device" sx={{ minWidth: 120 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </TextField>
              <TextField
                select size="small" value={programmeState}
                onChange={(e) => setProgrammeState(e.target.value as "" | "on" | "off")}
                label="Programme" sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="on">On programme</MenuItem>
                <MenuItem value="off">Not on programme</MenuItem>
              </TextField>
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <TextField
                select size="small" value={surveyQuestionFilter}
                onChange={(e) => {
                  setSurveyQuestionFilter(e.target.value);
                  setSurveyAnswerFilter("");
                  if (e.target.value && !loadSurveyAnswers) setLoadSurveyAnswers(true);
                }}
                label="Survey question" sx={{ minWidth: 220 }}
              >
                <MenuItem value="">All participants</MenuItem>
                {surveyQuestions.map((q) => <MenuItem key={q.id} value={q.id}>{q.title}</MenuItem>)}
              </TextField>
              {surveyQuestionFilter && (
                <TextField
                  size="small" label="Answer contains" value={surveyAnswerFilter}
                  onChange={(e) => setSurveyAnswerFilter(e.target.value)}
                  sx={{ minWidth: 160 }}
                />
              )}
              {!loadSurveyAnswers && (
                <Button size="small" variant="text" onClick={() => setLoadSurveyAnswers(true)}>
                  Load survey questions
                </Button>
              )}
              {surveyAnswersQuery.isLoading && (
                <CircularProgress size={16} sx={{ alignSelf: "center" }} />
              )}
            </Stack>
          </Box>
        </Collapse>

        {/* Summary + company breakdown chips */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="caption" color="text.secondary">
            {filteredParticipants.length}
            {filteredParticipants.length !== participants.length ? ` of ${participants.length}` : ""}{" "}
            participant{filteredParticipants.length !== 1 ? "s" : ""}
            {notOnProgrammeCount > 0 ? ` · ${notOnProgrammeCount} not on programme` : ""}
          </Typography>
          {companyCounts.map(([co, cnt]) => (
            <Chip
              key={co}
              label={`${co}: ${cnt}`}
              size="small"
              variant={companyFilter === co ? "filled" : "outlined"}
              color={companyFilter === co ? "primary" : "default"}
              onClick={() => setCompanyFilter(companyFilter === co ? "" : co)}
              sx={{ fontSize: 11, height: 22, cursor: "pointer" }}
            />
          ))}
        </Stack>
      </Stack>

      {/* Table */}
      {listQuery.isLoading ? (
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading participants…</Typography>
        </Stack>
      ) : listQuery.isError ? (
        <Alert severity="error">Failed to load participants.</Alert>
      ) : filteredParticipants.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="body2">
            {participants.length === 0 ? "No participants yet." : "No participants match your filters."}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{ "& th": { fontWeight: 600, fontSize: 12, py: 1.25, letterSpacing: 0 } }}
              >
                <TableCell>Participant</TableCell>
                <TableCell>Linked user</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Wear jeopardy</TableCell>
                <TableCell>Connected</TableCell>
                <TableCell>First connected</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Updated</TableCell>
              </TableRow>
              {/* In-table filter row */}
              <TableRow
                sx={{
                  "& td, & th": {
                    bgcolor: "action.hover",
                    py: 0.5,
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableCell>
                  <TextField
                    size="small" placeholder="Filter" fullWidth
                    value={headerFilters.participant}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, participant: e.target.value }))}
                    sx={{ "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" placeholder="Filter" fullWidth
                    value={headerFilters.linkedUser}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, linkedUser: e.target.value }))}
                    sx={{ "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small" placeholder="Filter" fullWidth
                    value={headerFilters.company}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, company: e.target.value }))}
                    sx={{ "& .MuiInputBase-input": { fontSize: 12, py: 0.5 } }}
                  />
                </TableCell>
                <TableCell>
                  <Select
                    size="small" displayEmpty fullWidth
                    value={headerFilters.status}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, status: e.target.value }))}
                    sx={{ fontSize: 12 }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>All</MenuItem>
                    {STATUS_OPTIONS.map((o) => <MenuItem key={o} value={o} sx={{ fontSize: 12 }}>{o}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    size="small" displayEmpty fullWidth
                    value={headerFilters.wearJeopardy}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, wearJeopardy: e.target.value }))}
                    sx={{ fontSize: 12 }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>All</MenuItem>
                    {Object.entries(wearStatusChipStyles).map(([k, v]) => (
                      <MenuItem key={k} value={k} sx={{ fontSize: 12 }}>{v.label}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Select
                    size="small" displayEmpty fullWidth
                    value={headerFilters.connected}
                    onChange={(e) => setHeaderFilters((p) => ({ ...p, connected: e.target.value }))}
                    sx={{ fontSize: 12 }}
                  >
                    <MenuItem value="" sx={{ fontSize: 12 }}>All</MenuItem>
                    {(Object.entries(connectedStatusStyles) as [ConnectedStatus, { label: string }][]).map(([k, v]) => (
                      <MenuItem key={k} value={k} sx={{ fontSize: 12 }}>{v.label}</MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell /><TableCell /><TableCell /><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredParticipants.map((p) => {
                const name = `${p.first_name ?? ""} ${p.surname ?? ""}`.trim();
                const initials = `${p.first_name?.[0] ?? ""}${p.surname?.[0] ?? ""}`.toUpperCase().slice(0, 2);
                const wearStyle = p.wear_jeopardy_status ? wearStatusChipStyles[p.wear_jeopardy_status] : null;
                return (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{ cursor: "pointer", "& td": { fontSize: 13, py: 1 } }}
                    onClick={() => navigate(`${ROUTES.PARTICIPANTS}/${p.id}`)}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar
                          sx={{ width: 30, height: 30, fontSize: 11, fontWeight: 700, bgcolor: "primary.main", flexShrink: 0 }}
                        >
                          {initials || "P"}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>
                            {name || "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap display="block">
                            {p.email || "No email"}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ fontSize: 13, color: p.user_email ? "text.primary" : "text.disabled" }}>
                        {p.user_email || "Not linked"}
                      </Typography>
                      {(p.user_first_name || p.user_last_name) && (
                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                          {p.user_first_name} {p.user_last_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        {p.company || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {p.status ? (
                        <Chip
                          label={p.status}
                          size="small"
                          sx={{ ...getStatusChipSx(p.status), fontSize: 11, height: 22 }}
                        />
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {wearStyle ? (
                        <Stack spacing={0.25}>
                          <Chip
                            label={wearStyle.label}
                            size="small"
                            sx={{ backgroundColor: wearStyle.bg, color: wearStyle.text, fontWeight: 600, fontSize: 11, height: 22 }}
                          />
                          {p.wear_jeopardy_status_reason && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                              {p.wear_jeopardy_status_reason}
                            </Typography>
                          )}
                        </Stack>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const cs = connectedStatusStyles[getConnectedStatus(p)];
                        return (
                          <Chip
                            label={cs.label}
                            size="small"
                            sx={{ backgroundColor: cs.bg, color: cs.text, fontWeight: 600, fontSize: 11, height: 22 }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDateTime(p.first_connected_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                        {p.flagged && <Chip label={p.flagged} size="small" sx={{ fontSize: 11, height: 20 }} />}
                        {/* Colour tag: render as a colour swatch, not the raw hex code (BT-198). */}
                        {isHexColour(p.color_tag) ? (
                          <Box
                            title={p.color_tag ?? undefined}
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              backgroundColor: p.color_tag as string,
                              border: "1px solid",
                              borderColor: "divider",
                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          p.color_tag && <Chip label={p.color_tag} size="small" sx={{ fontSize: 11, height: 20 }} />
                        )}
                        {!p.flagged && !p.color_tag && <Typography variant="caption" color="text.disabled">—</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Chip
                          label="WHOOP"
                          size="small"
                          variant={p.whoop_received ? "filled" : "outlined"}
                          sx={p.whoop_received
                            ? { bgcolor: "#ecfccb", color: "#365314", fontWeight: 600, fontSize: 11, height: 20 }
                            : { color: "text.disabled", fontSize: 11, height: 20 }}
                        />
                        <Chip
                          label="Own"
                          size="small"
                          variant={p.using_own_device ? "filled" : "outlined"}
                          sx={p.using_own_device
                            ? { bgcolor: "#dbeafe", color: "#1e3a8a", fontWeight: 600, fontSize: 11, height: 20 }
                            : { color: "text.disabled", fontSize: 11, height: 20 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {formatDateTime(p.updated_at)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
